import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="modal-cleanup"
export default class extends Controller {
  connect() {
    // Pass the function reference (not its return value) so the cleanup runs
    // when Turbo caches the page — not synchronously on connect/disconnect.
    // Keep a bound handler so removeEventListener can actually detach it.
    this.cleanup = () => window.closeAllModals?.()
    document.addEventListener('turbo:before-cache', this.cleanup)

    // When one #generalModal modal is turbo-swapped for another (publish confirm
    // → celebration → QR), the outgoing modal's backdrop (appended to <body>, so
    // it survives the innerHTML replace) is orphaned and stacks. Watch this
    // wrapper for content swaps and reconcile backdrops afterward: keep exactly
    // one while a modal is open, remove all + unlock the body when none is.
    this.observer = new MutationObserver(() => {
      // Defer so Bootstrap's own show/hide DOM work for the new modal settles
      // first, then reconcile.
      requestAnimationFrame(() => this.reconcileBackdrops())
    })
    this.observer.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.cleanup)
    this.observer?.disconnect()
  }

  reconcileBackdrops() {
    // A modal is present in the wrapper if #generalModal still holds a .modal
    // element (content-based, not the timing-sensitive .show class). While one
    // is present, keep a single backdrop (remove orphans from a swapped-out
    // predecessor); when the wrapper is empty, remove all + unlock the body.
    const hasModal = !!this.element.querySelector('.modal')
    const backdrops = document.querySelectorAll('.modal-backdrop')
    const keep = hasModal ? Math.max(backdrops.length - 1, 0) : backdrops.length
    for (let i = 0; i < keep; i++) backdrops[i].remove()
    if (!hasModal) {
      document.body.classList.remove('modal-open')
      document.body.style.removeProperty('overflow')
      document.body.style.removeProperty('padding-right')
    }
  }
}
